import { ElementRef, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { Store } from '@ngrx/store';

import { selectSelectedChannel } from '../../../store/app.selectors';

import { SnackBarService } from '../../../core/services/snackbar.service';
import { AttachmentTypes, IAttachment, WebappAttachmentConverter, WebAppAttachmentTypes } from '../interfaces/IAttachment';
import { ChannelAllowedFileTypes, ChannelFeatures, ChannelServiceType, IChannel } from '../interfaces/IChannel';
import { AppStateWithChat } from '../store/chat.reducer';
import { PermissionsService } from './permissions.service';

@Injectable()
export class AttachmentsService {
  selectedChannel: IChannel | null = null;
  constructor(
    private store: Store<AppStateWithChat>,
    private snackBarService: SnackBarService,
    private permissionsService: PermissionsService,
    private sanitizer: DomSanitizer
  ) {
    this.setChatSubscription();
  }

  setChatSubscription(): void {
    this.store.select(selectSelectedChannel).subscribe((selectedChannel) => {
      this.selectedChannel = selectedChannel;
    });
  }

  getTypeAttachment(type: string) {
    if (type.includes('image')) {
      return AttachmentTypes.IMAGE;
    } else if (type.includes('video')) {
      return AttachmentTypes.VIDEO;
    } else if (type.includes('audio')) {
      return AttachmentTypes.AUDIO;
    } else {
      return AttachmentTypes.DOCUMENT;
    }
  }

  isAllowedFileType(typeFile: string): boolean {
    if (!this.selectedChannel?.routing) {
      return true;
    }
    const types = ChannelAllowedFileTypes[this.selectedChannel.routing.toUpperCase() as ChannelServiceType];
    if (!types.length) {
      return true;
    }
    return types.some((type) => type.type.trim() === typeFile);
  }

  isAllowedFileSize(typeFile: string, sizeFile: number): boolean {
    if (!this.selectedChannel?.routing) {
      return true;
    }
    const types = ChannelAllowedFileTypes[this.selectedChannel.routing.toUpperCase() as ChannelServiceType];
    if (!types.length) {
      return true;
    }
    const option = types.find((type) => type.type.trim() === typeFile);
    return option ? option.size >= sizeFile : true;
  }

  allowedFileTypes(): string {
    if (this.selectedChannel?.routing) {
      return ChannelAllowedFileTypes[this.selectedChannel.routing.toUpperCase() as ChannelServiceType]
        .map((e) => e.type)
        .toString();
    } else {
      return '';
    }
  }

  onChange(event: Event, attachments: IAttachment[], uploader: ElementRef<HTMLElement>, onAdded?: () => void): void {
    if (this.permissionsService.checkChannelFeatures([ChannelFeatures.canSendAttachments])) {
      const files = (event.target as HTMLInputElement).files;
      if (files?.length) {
        for (const file of files) {
          this.setFileInAttachments(file, attachments, onAdded);
        }
      }
    }
    if (uploader.nativeElement instanceof HTMLInputElement) {
      uploader.nativeElement.value = '';
    }
  }

  onPaste(event: ClipboardEvent, attachments: IAttachment[], onAdded?: () => void) {
    if (this.permissionsService.checkChannelFeatures([ChannelFeatures.canSendAttachments])) {
      const clipboardData = event.clipboardData;
      if (clipboardData?.items?.length) {
        for (const file of clipboardData.items) {
          const blob = file.getAsFile();
          if (blob) {
            this.setFileInAttachments(blob, attachments, onAdded);
          }
        }
      }
    }
  }

  onDragOver(event: Event) {
    if (this.permissionsService.checkChannelFeatures([ChannelFeatures.canSendAttachments])) {
      event.preventDefault();
    }
  }

  onDrop(event: DragEvent, attachments: IAttachment[], onAdded?: () => void) {
    if (this.permissionsService.checkChannelFeatures([ChannelFeatures.canSendAttachments])) {
      event.preventDefault();
      const files = event.dataTransfer?.files;
      if (files?.length) {
        for (const file of files) {
          this.setFileInAttachments(file, attachments, onAdded);
        }
      }
    }
  }

  setFileInAttachments(file: File, attachments: IAttachment[], onAdded?: () => void): void {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (loadedEvent) => {
      if (!this.isAllowedFileType(file.type)) {
        this.snackBarService.showErrorSnackBar(`File type not allowed`);
        return;
      }
      if (!this.isAllowedFileSize(file.type, file.size)) {
        this.snackBarService.showErrorSnackBar(`File size not allowed`);
        return;
      }
      const uidAux = new Date().toISOString();
      const fileSource = loadedEvent.target?.result;
      if (fileSource == null) {
        return;
      }
      attachments.push({
        size: file.size,
        title: file.name,
        type: this.getTypeAttachment(file.type),
        thumbnailURL: fileSource,
        uid: uidAux,
        file: file,
      });
      onAdded?.();
    };
  }

  fallbackAttachments(attachments: IAttachment[]): IAttachment[] {
    if (!attachments?.length) {
      return [];
    }
    return attachments.map((e) => {
      if (!e.thumbnailURL) {
        e.thumbnailURL = e.highResolutionURL || e.lowResolutionURL || '';
      }
      if (e.type == AttachmentTypes.DOCUMENT) {
        const attachmentType =
          this.isAttachmentTypeFromUrl(e.thumbnailURL as string) || this.isAttachmentTypeFromUrl(e.title as string);
        if (attachmentType) {
          e.type = attachmentType;
        }
      }
      return e;
    });
  }

  getFileExtension(url: string): string | null {
    if (!url) {
      return null;
    }
    const match = url.match(/\.([0-9a-z]+)(?:[?#]|$)/i);
    return match ? match[1].toUpperCase() : null;
  }

  isAttachmentTypeFromUrl(url: string): AttachmentTypes | null {
    const extension = this.getFileExtension(url);
    if (extension && Object.values(WebAppAttachmentTypes).includes(extension as WebAppAttachmentTypes)) {
      return WebappAttachmentConverter[extension as WebAppAttachmentTypes] as AttachmentTypes;
    }
    return null;
  }
}
